/**
 * Pure camera and geometry helpers for the /monitor globe. The globe shows
 * one hemisphere at a time, so the overview faces it towards where the
 * screen's shipments are rather than a fixed point.
 */

import {
  wrapLngNear,
  type LngLat,
  type Padding,
} from '../../Home/map/monitorRouteShape';

type LatLng = { lat: number; lng: number };

const DEG = Math.PI / 180;
/** Mapbox world size at zoom 0; the globe's radius is worldSize / 2π. */
const TILE_SIZE = 512;
/** Shipments within this angle of their centre are framed, not just faced. */
const REGIONAL_SPREAD_DEG = 25;
/** The globe fills this share of the clear area on the overview. */
const GLOBE_FILL = 0.92;
/**
 * The overview faces the globe no further from the equator than this: a
 * northern-hemisphere fleet faced from its own centre would look down on
 * the Arctic and tip Asia over the horizon.
 */
export const MAX_OVERVIEW_LAT = 30;
/** Never zoom the overview further out than this. */
const MIN_OVERVIEW_ZOOM = 0.8;

const toVector = ({ lat, lng }: LatLng) => ({
  x: Math.cos(lat * DEG) * Math.cos(lng * DEG),
  y: Math.cos(lat * DEG) * Math.sin(lng * DEG),
  z: Math.sin(lat * DEG),
});

/**
 * The point on the sphere the positions cluster around (normalised vector
 * mean), so a fleet spread across the date line faces the Pacific, not
 * Africa. Null without positions or when they cancel out exactly.
 */
export const sphericalCentroid = (points: LatLng[]): LatLng | null => {
  if (points.length === 0) return null;
  const sum = points
    .map(toVector)
    .reduce((acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y, z: acc.z + v.z }), {
      x: 0,
      y: 0,
      z: 0,
    });
  const length = Math.hypot(sum.x, sum.y, sum.z);
  if (length < 1e-9) return null;
  return {
    lat: Math.asin(sum.z / length) / DEG,
    lng: Math.atan2(sum.y, sum.x) / DEG,
  };
};

/** Largest great-circle angle (degrees) from `center` to any of `points`. */
export const angularSpreadDeg = (points: LatLng[], center: LatLng): number => {
  const c = toVector(center);
  return points.reduce((max, p) => {
    const v = toVector(p);
    const dot = Math.max(-1, Math.min(1, c.x * v.x + c.y * v.y + c.z * v.z));
    return Math.max(max, Math.acos(dot) / DEG);
  }, 0);
};

/**
 * Zoom at which the globe's radius is `radiusPx` on screen when centred on
 * `centerLat`: Mapbox scales the globe to match the flat map's scale at the
 * centre, which grows by 1 / cos(latitude).
 */
export const globeZoomForRadius = (radiusPx: number, centerLat = 0): number =>
  Math.log2((2 * Math.PI * radiusPx * Math.cos(centerLat * DEG)) / TILE_SIZE);

export type GlobeOverview =
  | { kind: 'fit'; points: LatLng[] }
  | { kind: 'face'; center: LatLng; zoom: number };

/**
 * Overview camera: a regional fleet is framed like the flat map; anything
 * wider faces the whole globe towards the fleet, sized to the clear area
 * between the panels.
 */
export const globeOverview = (
  points: LatLng[],
  viewport: { width: number; height: number },
  padding: Padding
): GlobeOverview => {
  const clearWidth = Math.max(1, viewport.width - padding.left - padding.right);
  const clearHeight = Math.max(
    1,
    viewport.height - padding.top - padding.bottom
  );
  const radius = (Math.min(clearWidth, clearHeight) / 2) * GLOBE_FILL;
  const zoomAt = (lat: number) =>
    Math.max(MIN_OVERVIEW_ZOOM, globeZoomForRadius(radius, lat));
  const center = sphericalCentroid(points);
  if (!center) {
    return { kind: 'face', center: { lat: 20, lng: 0 }, zoom: zoomAt(20) };
  }
  if (angularSpreadDeg(points, center) <= REGIONAL_SPREAD_DEG) {
    // Longitudes next to the centre, so a fleet astride the date line is
    // framed across it rather than round the world.
    return {
      kind: 'fit',
      points: points.map((p) => ({
        lat: p.lat,
        lng: wrapLngNear(p.lng, center.lng),
      })),
    };
  }
  const lat = Math.max(
    -MAX_OVERVIEW_LAT,
    Math.min(MAX_OVERVIEW_LAT, center.lat)
  );
  return { kind: 'face', center: { lat, lng: center.lng }, zoom: zoomAt(lat) };
};

const wrap = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * A continuous-longitude line (it may run past ±180, see monitorRouteShape)
 * as MultiLineString parts within [-180, 180], split where it crosses the
 * date line, so the globe never draws it the long way round.
 */
export const toWrappedMultiLine = (line: LngLat[]): LngLat[][] => {
  const parts: LngLat[][] = [];
  let current: LngLat[] = [];
  line.forEach(([lng, lat], i) => {
    if (i === 0) {
      current.push([wrap(lng), lat]);
      return;
    }
    const [prevLng, prevLat] = line[i - 1];
    const prevCopy = Math.floor((prevLng + 180) / 360);
    const copy = Math.floor((lng + 180) / 360);
    if (copy !== prevCopy) {
      // Crosses ±180 between these two points: end and restart there.
      const edge = (copy > prevCopy ? copy : prevCopy) * 360 - 180;
      const t = (edge - prevLng) / (lng - prevLng);
      const crossLat = prevLat + (lat - prevLat) * t;
      const east = copy > prevCopy;
      current.push([east ? 180 : -180, crossLat]);
      parts.push(current);
      current = [[east ? -180 : 180, crossLat]];
    }
    current.push([wrap(lng), lat]);
  });
  if (current.length) parts.push(current);
  return parts.filter((part) => part.length >= 2);
};

/** Keeps the part of an open ring on one side of the meridian `edge`. */
const clipRing = (
  ring: LngLat[],
  edge: number,
  keepWest: boolean
): LngLat[] => {
  const inside = ([lng]: LngLat) => (keepWest ? lng <= edge : lng >= edge);
  const cross = ([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): LngLat => [
    edge,
    lat1 + ((lat2 - lat1) * (edge - lng1)) / (lng2 - lng1),
  ];
  const out: LngLat[] = [];
  ring.forEach((point, i) => {
    const prev = ring[(i + ring.length - 1) % ring.length];
    if (inside(point)) {
      if (!inside(prev)) out.push(cross(prev, point));
      out.push(point);
    } else if (inside(prev)) {
      out.push(cross(prev, point));
    }
  });
  return out;
};

/**
 * A closed [lng, lat] polygon ring as one or two rings within [-180, 180]:
 * an area spanning the date line (179 to -179) is cut there, so it is not
 * filled the long way round the globe.
 */
export const splitRingAtAntimeridian = (ring: LngLat[]): LngLat[][] => {
  const open =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;
  if (open.length < 3) return [];
  // Each point next to the one before, so the ring never jumps 360 degrees.
  const unwrapped: LngLat[] = [];
  open.forEach(([lng, lat]) => {
    const prev = unwrapped[unwrapped.length - 1];
    unwrapped.push([prev ? wrapLngNear(lng, prev[0]) : lng, lat]);
  });
  const lngs = unwrapped.map(([lng]) => lng);
  const close = (part: LngLat[]) =>
    part.length >= 3 ? [[...part, part[0]]] : [];
  if (Math.max(...lngs) > 180) {
    return [
      ...close(clipRing(unwrapped, 180, true)),
      ...close(
        clipRing(unwrapped, 180, false).map(([lng, lat]): LngLat => [
          lng - 360,
          lat,
        ])
      ),
    ];
  }
  if (Math.min(...lngs) < -180) {
    return [
      ...close(
        clipRing(unwrapped, -180, true).map(([lng, lat]): LngLat => [
          lng + 360,
          lat,
        ])
      ),
      ...close(clipRing(unwrapped, -180, false)),
    ];
  }
  return close(open);
};

/** How far ahead (degrees of arc) the heading is sampled on screen. */
const HEADING_SAMPLE_DEG = 0.5;

/**
 * The direction of travel as drawn on screen, in degrees clockwise from up.
 * On a globe, north is only straight up at the centre of the view, so the
 * geographic heading is turned into the angle between the shipment and a
 * point just ahead of it on that heading, both as projected now.
 */
export const screenHeadingDeg = (
  project: (lngLat: LngLat) => { x: number; y: number },
  position: LatLng,
  headingDeg: number
): number => {
  const lat1 = position.lat * DEG;
  const lng1 = position.lng * DEG;
  const bearing = headingDeg * DEG;
  const arc = HEADING_SAMPLE_DEG * DEG;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(arc) +
      Math.cos(lat1) * Math.sin(arc) * Math.cos(bearing)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(arc) * Math.cos(lat1),
      Math.cos(arc) - Math.sin(lat1) * Math.sin(lat2)
    );
  const from = project([position.lng, position.lat]);
  const to = project([lng2 / DEG, lat2 / DEG]);
  return Math.atan2(to.x - from.x, from.y - to.y) / DEG;
};
